import { useCallback } from "react";
import { useToastHelpers } from "@/hooks/useToast";
import {
  useWebSocketMessageRouter,
  useWebSocketEvent,
} from "@/hooks/useWebSocketMessageRouter";
import {
  XPAwardMessage,
  GenerationProgressMessage,
  TranslationProgressMessage,
  GeneralNotificationMessage,
} from "@/contexts/WebSocketTypes";

/**
 * Centralized WebSocket notification handler using the event-based router system.
 * This prevents duplicate processing and provides stable message handling.
 */
export const useWebSocketNotifications = () => {
  const toastHelpers = useToastHelpers();

  // Initialize the centralized message router
  const { WEBSOCKET_EVENTS } = useWebSocketMessageRouter();

  // Handle XP award notifications with stable callback
  const handleXPAward = useCallback(
    (data: XPAwardMessage) => {
      // Display appropriate toast notification based on XP award type
      if (data.is_welcome_bonus) {
        toastHelpers.welcomeBonus(data.xp_earned, data.activity_description);
      } else if (data.level_up && data.old_level && data.new_level) {
        toastHelpers.levelUp(
          data.xp_earned,
          data.activity_description,
          data.old_level,
          data.new_level,
        );
      } else {
        toastHelpers.xp(data.xp_earned, data.activity_description);
      }
    },
    [toastHelpers],
  );

  // Handle generation progress notifications with stable callback
  const handleGenerationProgress = useCallback(
    (data: GenerationProgressMessage) => {
      switch (data.status) {
        case "started":
          toastHelpers.info("Generation Started", data.message);
          break;
        case "completed":
          toastHelpers.success("Generation Complete", data.message);
          break;
        case "error":
          toastHelpers.error("Generation Error", data.message);
          break;
        case "in_progress":
          // Avoid toast spam for progress updates
          break;
      }
    },
    [toastHelpers],
  );

  // Handle translation progress notifications with stable callback
  const handleTranslationProgress = useCallback(
    (data: TranslationProgressMessage) => {
      switch (data.status) {
        case "started":
          toastHelpers.info(
            "Translation Started",
            `${data.message} (${data.lang})`,
          );
          break;
        case "completed":
          toastHelpers.success(
            "Translation Complete",
            `${data.message} (${data.lang})`,
          );
          break;
        case "error":
          toastHelpers.error(
            "Translation Error",
            `${data.message} (${data.lang})`,
          );
          break;
        case "in_progress":
          // Avoid toast spam for progress updates
          break;
      }
    },
    [toastHelpers],
  );

  // Handle general notifications with stable callback
  const handleGeneralNotification = useCallback(
    (data: GeneralNotificationMessage) => {
      const VARIANT_MAP = {
        success: () => toastHelpers.success(data.title, data.message),
        error: () => toastHelpers.error(data.title, data.message),
        warning: () => toastHelpers.warning(data.title, data.message),
        info: () => toastHelpers.info(data.title, data.message),
      } as const;

      const handler = VARIANT_MAP[data.variant];
      if (handler) {
        handler();
      } else {
        // Fallback for unknown variants
        toastHelpers.info(data.title, data.message);
      }
    },
    [toastHelpers],
  );

  // Subscribe to WebSocket events using the centralized router
  useWebSocketEvent(WEBSOCKET_EVENTS.XP_AWARD, handleXPAward, [handleXPAward]);

  useWebSocketEvent(
    WEBSOCKET_EVENTS.GENERATION_PROGRESS,
    handleGenerationProgress,
    [handleGenerationProgress],
  );

  useWebSocketEvent(
    WEBSOCKET_EVENTS.TRANSLATION_PROGRESS,
    handleTranslationProgress,
    [handleTranslationProgress],
  );

  useWebSocketEvent(
    WEBSOCKET_EVENTS.GENERAL_NOTIFICATION,
    handleGeneralNotification,
    [handleGeneralNotification],
  );
};
